/**
 * Bản tiếng Việt cho lớp "gần tôi" (near me) — 9 dịch vụ chủ lực.
 *
 * Biên dịch thủ công (không tốn AI credit khi build hay chạy). Nội dung khớp
 * với ./registry.ts và ./en-copy.ts:
 *  - Thái Lan KHÔNG phải thành viên Công ước Hague Apostille, nên giấy tờ Thái
 *    dùng ở nước ngoài phải hợp pháp hóa tại Cục Lãnh sự, Bộ Ngoại giao Thái Lan
 *    và, khi cơ quan tiếp nhận yêu cầu, chứng nhận thêm tại đại sứ quán nước đến
 *    đặt tại Thái Lan.
 *  - Không niêm yết giá (báo giá theo từng hồ sơ), không cam kết thời gian và
 *    không bảo đảm được chấp nhận — quyền quyết định thuộc cơ quan tiếp nhận.
 */

import type { NearServiceKey } from "./registry";

export type NearViCopy = {
  /** Tên dịch vụ ngắn dùng cho H1/title. */
  label: string;
  /** Định nghĩa 1-2 câu, dùng trong khối trả lời trước. */
  definition: string;
  /** Trình tự thực hiện. */
  chain: string[];
  /** Giấy tờ cần chuẩn bị. */
  checklist: string[];
  /** Lỗi thường gặp khiến hồ sơ bị trả lại. */
  pitfalls: string[];
  /** Phạm vi xử lý từ xa. */
  remote: string;
};

export const NEAR_VI_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export type NearViKey = (typeof NEAR_VI_KEYS)[number];

const MFA =
  "Khi nơi tiếp nhận yêu cầu, hồ sơ đi tiếp tới Cục Lãnh sự (Bộ Ngoại giao Thái Lan) rồi tới đại sứ quán nước đến tại Thái Lan.";

export const NEAR_VI_COPY: Record<NearViKey, NearViCopy> = {
  notary: {
    label: "Luật sư công chứng Thái Lan (Notarial Services Attorney)",
    definition:
      "Thái Lan không có văn phòng công chứng viên riêng biệt. Việc chứng nhận chữ ký, bản sao, bản tuyên thệ (affidavit) và văn bản ủy quyền doanh nghiệp do luật sư được Hội đồng Luật sư Thái Lan cấp phép làm Notarial Services Attorney thực hiện, chủ yếu để dùng ở nước ngoài.",
    chain: [
      "Gửi bản scan màu toàn bộ hồ sơ và cho biết cơ quan tiếp nhận hoặc quốc gia sử dụng.",
      "Chúng tôi xác định trang nào thực sự cần chứng nhận và theo thứ tự nào.",
      "Người ký ký trước mặt luật sư công chứng (trực tiếp, hoặc theo hình thức mà nơi tiếp nhận chấp nhận).",
      "Ghi lời chứng, đóng dấu và vào sổ đăng ký.",
      MFA,
      "Bản gốc đã chứng nhận được chuyển phát hoặc nhận tại văn phòng.",
    ],
    checklist: [
      "Hộ chiếu hoặc CCCD Thái của từng người ký (còn hạn, viết tên trùng khớp giấy tờ).",
      "Văn bản cần chứng nhận ở bản cuối cùng, chưa ký.",
      "Trích lục doanh nghiệp (DBD) và danh sách giám đốc có thẩm quyền nếu ký nhân danh công ty.",
      "Giấy ủy quyền nếu ký thay người khác.",
      "Bản yêu cầu bằng văn bản của cơ quan tiếp nhận, nếu bạn đã có.",
    ],
    pitfalls: [
      "Ký trước buổi hẹn chứng nhận, buộc phải làm lại bản gốc mới.",
      "Cách viết tên khác với hộ chiếu — nguyên nhân bị trả hồ sơ phổ biến nhất ở nước ngoài.",
      "Tưởng rằng giấy tờ Thái có thể xin Apostille; thực tế phải qua hợp pháp hóa Bộ Ngoại giao và đại sứ quán.",
      "Chứng nhận bản sao trong khi nơi tiếp nhận yêu cầu bản gốc có chứng nhận.",
      "Trích lục doanh nghiệp quá cũ, trong khi nhiều cơ quan đòi bản cấp gần đây.",
    ],
    remote:
      "Rà soát hồ sơ, kiểm tra lời chứng và các bước chứng nhận tiếp theo làm từ xa được; chỉ khâu ký cần có mặt.",
  },
  naati: {
    label: "Dịch thuật công chứng NAATI",
    definition:
      "Bản dịch NAATI do biên dịch viên có chứng chỉ NAATI (cơ quan công nhận biên phiên dịch của Úc) thực hiện, kèm lời chứng và con dấu của biên dịch viên — thường là yêu cầu bắt buộc của cơ quan chính phủ Úc, trường đại học và trung tâm xét duyệt visa.",
    chain: [
      "Gửi bản scan màu; chúng tôi kiểm tra độ rõ, con dấu và số trang.",
      "Xác nhận cơ quan tiếp nhận (Home Affairs, trường đại học, hội đồng cấp phép) và cách viết tên theo hộ chiếu.",
      "Biên dịch viên NAATI thực hiện bản dịch kèm lời chứng và dấu.",
      "Đối chiếu chất lượng với bản gốc: tên, ngày tháng, con số, chữ trên dấu.",
      "Giao bản điện tử có chứng nhận và, nếu cần, bản giấy gửi bưu điện.",
    ],
    checklist: [
      "Trang hộ chiếu để lấy đúng cách viết tên bằng chữ Latin.",
      "Bản scan màu đầy đủ từng giấy tờ, gồm mặt sau và con dấu.",
      "Bản dịch đã nộp trước đó (nếu có) để giữ thống nhất thuật ngữ.",
      "Yêu cầu về bản dịch của cơ quan tiếp nhận, nếu bạn được cung cấp.",
    ],
    pitfalls: [
      "Scan bị cắt mất con dấu, khiến bộ bản dịch không hợp lệ.",
      "Cùng một tên nhưng viết khác nhau giữa các giấy tờ.",
      "Dịch giấy tờ trong khi cơ quan cần bản gốc có chứng nhận trước.",
      "Gửi bản đen trắng khiến không kiểm chứng được màu con dấu.",
    ],
    remote: "Toàn bộ quy trình làm trực tuyến — không cần đi lại.",
  },
  sworn: {
    label: "Biên dịch viên tuyên thệ / đăng ký tại tòa",
    definition:
      "Nhiều nước chỉ chấp nhận bản dịch của biên dịch viên tuyên thệ hoặc có đăng ký chính thức. Tại Thái Lan, việc này do biên dịch viên nằm trong danh sách của cơ quan hoặc tòa án liên quan thực hiện, với trang lời chứng nêu rõ danh tính biên dịch viên và tài liệu nguồn.",
    chain: [
      "Xác nhận nơi đến công nhận danh sách nào (danh sách tòa án, đại sứ quán hay bộ ngành).",
      "Rà soát bản scan về độ rõ và tính đầy đủ.",
      "Biên dịch viên có đăng ký thực hiện bản dịch và trang lời chứng.",
      "Tùy chọn hợp pháp hóa tại Bộ Ngoại giao và chứng nhận tại đại sứ quán nước đến.",
      "Gửi trả bộ bản giấy đã đóng dấu.",
    ],
    checklist: [
      "Hộ chiếu hoặc CCCD để phiên âm tên chính xác.",
      "Bản scan đầy đủ, gồm phụ lục và các con dấu.",
      "Tên cơ quan tiếp nhận và cặp ngôn ngữ cần dịch.",
      "Thời hạn nộp mà cơ quan tiếp nhận ấn định.",
    ],
    pitfalls: [
      "Dùng biên dịch viên thường trong khi nơi đến chỉ chấp nhận người có đăng ký.",
      "Thiếu trang lời chứng — đúng phần mà cơ quan kiểm tra đầu tiên.",
      "Dùng lẫn lộn nhiều kiểu phiên âm giữa các giấy tờ.",
      "Thiếu trang phụ lục mà tài liệu gốc có dẫn chiếu.",
    ],
    remote:
      "Hầu hết các bước chúng tôi làm thay bạn; chỉ bước ký trực tiếp mới cần bạn có mặt.",
  },
  pcc: {
    label: "Phiếu lý lịch tư pháp Thái Lan (Police Clearance Certificate)",
    definition:
      "Phiếu lý lịch tư pháp Thái Lan do Cảnh sát Hoàng gia Thái Lan cấp, ghi nhận việc có hay không có tiền án tiền sự trong cơ sở dữ liệu Thái Lan. Cơ quan visa, thường trú, cấp phép và tuyển dụng ở nước ngoài thường yêu cầu, kèm bản dịch có chứng nhận.",
    chain: [
      "Xác nhận cơ quan yêu cầu và mục đích sẽ ghi trong đơn.",
      "Chuẩn bị hồ sơ: giấy tờ tùy thân, chứng cứ mục đích sử dụng, ảnh.",
      "Lấy dấu vân tay — phải làm trực tiếp hoặc theo hình thức ở nước ngoài được chấp nhận.",
      "Nộp lên Cảnh sát Hoàng gia Thái Lan và theo dõi tình trạng.",
      "Dịch có chứng nhận, sau đó hợp pháp hóa Bộ Ngoại giao và đại sứ quán nếu cần.",
    ],
    checklist: [
      "Hộ chiếu (và CCCD Thái hoặc giấy phép lao động nếu có).",
      "Chứng cứ mục đích — thư visa, yêu cầu của nhà tuyển dụng hoặc mẫu đơn của cơ quan.",
      "Ảnh mới theo đúng quy cách được yêu cầu.",
      "Giấy tờ cư trú hoặc xuất nhập cảnh nếu người xin là người nước ngoài.",
      "Giấy ủy quyền nếu nộp thay.",
    ],
    pitfalls: [
      "Ghi mục đích không khớp cơ quan yêu cầu, có thể khiến phiếu không dùng được.",
      "Phiếu vân tay lấy sai định dạng được chấp nhận.",
      "Phiếu cấp quá lâu trước khi nộp, khi cơ quan có quy định thời hạn hiệu lực.",
      "Quên rằng khâu dịch và hợp pháp hóa cần thêm thời gian riêng.",
    ],
    remote:
      "Lấy vân tay phải trực tiếp; phần nộp, theo dõi, dịch và hợp pháp hóa chúng tôi làm thay bạn.",
  },
  translation: {
    label: "Dịch thuật có chứng nhận (mọi ngôn ngữ)",
    definition:
      "Bản dịch có chứng nhận là bản dịch đầy đủ kèm lời chứng có chữ ký xác nhận nội dung đúng với bản gốc. Ai được quyền chứng nhận — công ty dịch thuật, biên dịch viên có đăng ký hay biên dịch viên trong danh sách đại sứ quán — do cơ quan tiếp nhận quy định.",
    chain: [
      "Rà soát bản scan và báo giá theo từng hồ sơ.",
      "Chốt thuật ngữ và cách phiên âm tên theo hộ chiếu.",
      "Dịch, sau đó hiệu đính độc lập đối chiếu bản gốc.",
      "Phát hành và đóng dấu trang lời chứng.",
      "Tùy chọn hợp pháp hóa Bộ Ngoại giao và chứng nhận đại sứ quán nước đến.",
    ],
    checklist: [
      "Bản scan màu mọi trang, cả mặt trước và mặt sau.",
      "Trang hộ chiếu để lấy cách viết tên.",
      "Ngôn ngữ đích và cơ quan tiếp nhận.",
      "Bản dịch trước đây, để giữ thống nhất cách diễn đạt.",
    ],
    pitfalls: [
      "Dịch thiếu trang của tài liệu nhiều trang.",
      "Dịch trước khi đóng dấu chính thức, khiến con dấu không xuất hiện trong bản dịch.",
      "Tên trên giấy chứng nhận, hộ chiếu và mẫu đơn không khớp nhau.",
      "Tưởng một loại chứng nhận dùng được ở mọi quốc gia — yêu cầu mỗi nơi mỗi khác.",
    ],
    remote: "Hoàn toàn trực tuyến, bản giấy gửi bưu điện khi cần bản gốc.",
  },
  moj: {
    label: "Dịch vụ giấy tờ Bộ Tư pháp và cơ quan nhà nước",
    definition:
      "Các thủ tục tại Bộ Tư pháp và cơ quan liên quan gồm trích lục hồ sơ, đơn từ và chứng nhận do cơ quan Thái Lan xử lý. Mỗi thủ tục có bộ mẫu, danh mục chứng cứ và hàng đợi riêng, nên đi đúng thứ tự quan trọng hơn đi nhanh.",
    chain: [
      "Xác định chính xác cơ quan, phòng ban và mẫu đơn cần dùng.",
      "Tập hợp danh mục chứng cứ và kiểm tra giấy tờ tùy thân.",
      "Nộp hồ sơ và ghi lại số tham chiếu.",
      "Theo dõi tiến độ và phản hồi khi cơ quan yêu cầu bổ sung.",
      "Dịch có chứng nhận và hợp pháp hóa khi giấy tờ mang ra nước ngoài.",
    ],
    checklist: [
      "Hộ chiếu hoặc CCCD Thái của người nộp.",
      "Sổ hộ khẩu hoặc giấy tờ doanh nghiệp, tùy loại thủ tục.",
      "Chứng cứ kèm theo nêu trong mẫu của cơ quan.",
      "Giấy ủy quyền nếu nộp thay.",
    ],
    pitfalls: [
      "Nộp sai phòng ban, phải xếp hàng lại từ đầu.",
      "Thiếu chứng cứ — nguyên nhân chậm trễ thường gặp nhất.",
      "Bỏ qua thời hạn hiệu lực của giấy tờ kèm theo.",
      "Dịch trước khi bản gốc tiếng Thái chốt xong.",
    ],
    remote:
      "Nộp và theo dõi chúng tôi làm thay bạn ở những nơi cho phép đại diện.",
  },
  visa: {
    label: "Chuẩn bị hồ sơ visa và xuất nhập cảnh",
    definition:
      "Công việc hồ sơ visa là chuẩn bị và tư vấn giấy tờ: tập hợp, dịch và chứng nhận bộ hồ sơ mà lãnh sự quán hoặc cơ quan xuất nhập cảnh yêu cầu. Quyền quyết định luôn thuộc về cơ quan xét duyệt.",
    chain: [
      "Xác nhận loại visa và danh mục giấy tờ đang công bố hiện hành.",
      "So sánh với những gì bạn đã có để tìm phần còn thiếu.",
      "Dịch có chứng nhận và chứng nhận cho các giấy tờ cần thiết.",
      "Sắp xếp hồ sơ theo đúng thứ tự cơ quan liệt kê, kèm bảng kiểm khi nộp.",
      "Hỗ trợ trong lúc nộp và phản hồi khi bị yêu cầu bổ sung.",
    ],
    checklist: [
      "Hộ chiếu còn đủ hạn và các trang visa trước đây.",
      "Giấy tờ hộ tịch — kết hôn, khai sinh, ly hôn — nếu loại visa yêu cầu.",
      "Chứng minh tài chính theo đúng định dạng cơ quan quy định.",
      "Thư việc làm, học tập hoặc bảo lãnh.",
      "Lý lịch tư pháp và giấy khám sức khỏe khi cần.",
    ],
    pitfalls: [
      "Dùng danh mục giấy tờ cũ trên blog thay vì bản hiện hành của cơ quan.",
      "Chứng minh tài chính sai định dạng hoặc sai kỳ hạn.",
      "Chứng nhận sai thứ tự, khiến lời chứng không bao trùm bản cuối cùng.",
      "Kỳ vọng được cam kết đậu — không đơn vị tư vấn nào có thể cam kết.",
    ],
    remote:
      "Chuẩn bị và rà soát làm từ xa; phỏng vấn và lấy sinh trắc học phải có mặt.",
  },
  lgz: {
    label: "Hợp pháp hóa tại Bộ Ngoại giao Thái Lan (Cục Lãnh sự)",
    definition:
      "Hợp pháp hóa tại Cục Lãnh sự, Bộ Ngoại giao Thái Lan là việc chứng nhận chữ ký và con dấu trên giấy tờ chính thức của Thái Lan và bản dịch của chúng. Do Thái Lan không phải thành viên Apostille, đây là bước chuẩn trước khi đại sứ quán nước đến chứng nhận hồ sơ.",
    chain: [
      "Xác nhận nơi đến cần hợp pháp hóa bản gốc tiếng Thái, bản dịch, hay cả hai.",
      "Xin bản gốc mới nếu cơ quan yêu cầu giấy cấp gần đây.",
      "Chuẩn bị bản dịch có chứng nhận, dịch cả nội dung con dấu.",
      "Nộp tại Cục Lãnh sự và nhận kết quả.",
      "Chứng nhận tại đại sứ quán nước đến khi cơ quan tiếp nhận yêu cầu.",
    ],
    checklist: [
      "Bản gốc chính thức của Thái Lan có chữ ký và con dấu.",
      "Hộ chiếu hoặc CCCD của người đứng tên giấy tờ.",
      "Bản dịch có chứng nhận, nếu nơi đến cần bộ đã dịch.",
      "Giấy ủy quyền để nộp thay.",
    ],
    pitfalls: [
      "Chờ Apostille cho giấy tờ Thái — Thái Lan không tham gia công ước này.",
      "Bản dịch bỏ sót phần chữ trên con dấu.",
      "Nộp bản photo trong khi cần bản gốc của cơ quan cấp.",
      "Hợp pháp hóa trước khi nơi đến xác nhận cần bộ nào, dẫn tới phải làm lại.",
    ],
    remote: "Nộp và nhận kết quả chúng tôi làm thay; bạn chỉ cần gửi bản gốc.",
  },
  apostille: {
    label: "Apostille và hợp pháp hóa cho giấy tờ Thái Lan",
    definition:
      "Apostille là một chứng nhận duy nhất dùng giữa các nước thành viên Công ước Hague. Thái Lan không phải thành viên, nên giấy tờ Thái phải hợp pháp hóa tại Cục Lãnh sự và, khi cần, chứng nhận tại đại sứ quán nước đến ở Thái Lan. Giấy tờ nước ngoài đã có Apostille vẫn có thể cần dịch và chứng nhận để dùng tại Thái Lan.",
    chain: [
      "Xác định chiều sử dụng: giấy tờ Thái đi nước ngoài, hay giấy tờ nước ngoài vào Thái Lan.",
      "Hồ sơ đi ra: dịch có chứng nhận, hợp pháp hóa Bộ Ngoại giao, rồi chứng nhận đại sứ quán nếu cần.",
      "Hồ sơ đi vào: kiểm tra Apostille nước ngoài, rồi dịch tiếng Thái có chứng nhận và chứng nhận phía Thái nếu cần.",
      "Xác nhận yêu cầu về câu chữ hiện hành của cơ quan tiếp nhận trước khi nộp.",
      "Bàn giao bộ hồ sơ hoàn chỉnh kèm bảng kiểm khi nộp.",
    ],
    checklist: [
      "Giấy tờ ở tình trạng chứng nhận hiện tại, gồm cả Apostille nước ngoài nếu có.",
      "Hộ chiếu của người đứng tên để đối chiếu tên.",
      "Yêu cầu bằng văn bản của cơ quan tiếp nhận, nếu có.",
      "Thông tin quốc gia đến và mục đích sử dụng.",
    ],
    pitfalls: [
      "Trả tiền cho 'Apostille' trên giấy tờ Thái — loại chứng nhận này không thể cấp.",
      "Tưởng Apostille nước ngoài thay thế được bản dịch tiếng Thái có chứng nhận.",
      "Bỏ qua chứng nhận đại sứ quán khi cơ quan tiếp nhận vẫn yêu cầu.",
      "Dựa vào hướng dẫn cũ trong khi quy định đã thay đổi.",
    ],
    remote:
      "Đánh giá và toàn bộ chuỗi chứng nhận làm từ xa; bạn chỉ cần chuyển bản gốc tới chúng tôi.",
  },
};

/** Tên tỉnh/khu vực Thái Lan viết theo tiếng Việt. */
export const NEAR_VI_AREA: Record<string, string> = {
  nonthaburi: "Nonthaburi",
  pathumthani: "Pathum Thani",
  samutprakan: "Samut Prakan",
  chonburi: "Chonburi",
  pattaya: "Pattaya",
  rayong: "Rayong",
  chiangmai: "Chiang Mai",
  chiangrai: "Chiang Rai",
  khonkaen: "Khon Kaen",
  udonthani: "Udon Thani",
  nakhonratchasima: "Nakhon Ratchasima (Korat)",
  phuket: "Phuket",
  suratthani: "Surat Thani",
  songkhla: "Songkhla",
  ubonratchathani: "Ubon Ratchathani",
};
